import { nextReference } from './cashBookGrid.js';

/**
 * Generated references — the screen's side of migration 065.
 *
 * THE SERVER IS THE AUTHORITY. It assigns `PREFIX + MM + 5 digits` on save when
 * a line arrives with no reference and a prefix exists for the batch's bank,
 * direction and financial year, and it closes gaps when a draft line is removed,
 * abandoned or re-dated. Nothing here invents or counts a generated number: the
 * grid shows `GET /cash-batches/:id/next-reference` as a grey hint and sends a
 * blank reference as null. This file only decides what the draft row does with
 * the answer, and checks a prefix as it is typed, which the server then checks
 * again (`validatePrefix`).
 *
 * WHEN THERE IS NO PREFIX the screen behaves exactly as it did before 065:
 * `nextReference` (cashBookGrid.js) counts the typed reference up client-side.
 */

/** Will the server generate the reference for a blank one? Only on GENERATED. */
export const isAutoPreview = (preview) =>
  Boolean(preview) && preview.reason === 'GENERATED' && Boolean(preview.reference);

/**
 * What the Reference input shows while the draft is blank.
 * `placeholder` is the previewed number; `title` carries the server's own
 * sentence when it declined to preview (e.g. the sequence is exhausted).
 */
export function referenceHint(preview, fallbackPlaceholder = 'P27-0206') {
  if (isAutoPreview(preview)) {
    return {
      auto: true,
      placeholder: `${preview.reference} (auto)`,
      title: 'Leave blank and this number is assigned when the line is saved. '
        + 'Type a reference to use your own instead.',
    };
  }
  return {
    auto: false,
    placeholder: fallbackPlaceholder,
    title: preview?.reason === 'EXHAUSTED' && preview.message ? preview.message : undefined,
  };
}

/** The `date` for next-reference, or null when the draft date is not yet a whole date. */
export const previewDate = (date) =>
  (/^\d{4}-\d{2}-\d{2}$/.test(String(date ?? '').trim()) ? String(date).trim() : null);

export const nextReferenceUrl = (batchId, date) => {
  const d = previewDate(date);
  return `/api/cash-batches/${batchId}/next-reference${d ? `?date=${d}` : ''}`;
};

/**
 * What the draft row's Reference holds after a line was saved.
 *
 * With a prefix it is EMPTY — the server numbers the next one, and a typed
 * reference on the line just saved must not seed one. Without, it is today's
 * behaviour: the saved line's reference counted up by one.
 */
export const referenceAfterSave = (auto, savedReference) =>
  (auto ? '' : nextReference(savedReference));

/**
 * The preview arrived (or the date moved it) and the mode may have flipped.
 *
 * `seed` is what the client-side counter would have put there. The field is only
 * touched when it still holds exactly that, or nothing: a reference somebody
 * typed is theirs and a mode change never overwrites it.
 *   manual -> auto : a field still holding the seed is emptied.
 *   auto -> manual : an empty field takes the seed (today's behaviour).
 */
export function referenceOnModeChange({ current, seed, wasAuto, nowAuto }) {
  if (wasAuto === nowAuto) return current;
  const cur = String(current ?? '');
  if (nowAuto) return cur === String(seed ?? '') ? '' : cur;
  return cur === '' ? String(seed ?? '') : cur;
}

/**
 * After the lines were reloaded (a delete, an abandon or a re-date renumbers the
 * draft lines above it), a row somebody is part-way through editing still holds
 * the OLD reference, and `patchBody` would send it back as if they had typed it.
 * A row whose reference still equals what its line used to say is moved to what
 * the server now says; one they actually changed is left alone. Rows for lines
 * that no longer exist are dropped.
 */
export function rebaseDraftReferences(drafts, oldLines, newLines) {
  const was = new Map((oldLines ?? []).map((l) => [l.id, l]));
  const now = new Map((newLines ?? []).map((l) => [l.id, l]));
  const out = {};
  for (const [id, row] of Object.entries(drafts ?? {})) {
    const fresh = now.get(id);
    if (!fresh) continue;
    const old = was.get(id);
    const untouched = old && String(row.reference ?? '') === String(old.reference ?? '');
    out[id] = untouched && (old.reference ?? '') !== (fresh.reference ?? '')
      ? { ...row, reference: fresh.reference ?? '' } : row;
  }
  return out;
}

// ------------------------------------------------- the Reference prefixes ----

export const PREFIX_MIN = 2;
export const PREFIX_MAX = 12;

/** What a prefix input keeps as you type: capitals and digits, nothing else. */
export const normalisePrefixInput = (text) =>
  String(text ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, PREFIX_MAX);

/** A sentence when the prefix would be refused, else null. The server's own rules. */
export function prefixProblem(prefix) {
  const p = String(prefix ?? '');
  if (p === '') return 'a reference prefix is required';
  if (p.length < PREFIX_MIN || p.length > PREFIX_MAX) {
    return `a reference prefix is ${PREFIX_MIN} to ${PREFIX_MAX} characters long`;
  }
  if (!/^[A-Z0-9]+$/.test(p)) return 'capital letters and digits only';
  if (!/[A-Z]$/.test(p)) return 'a reference prefix must end in a letter, not a digit';
  return null;
}

/** `PACHQ` -> `PACHQ09` + `00001`, shown while somebody types. Null until it is valid. */
export function referenceExample(prefix, month = '09') {
  if (prefixProblem(prefix)) return null;
  return { head: `${prefix}${month}`, tail: '00001', full: `${prefix}${month}00001` };
}

/** The status banner's sentence, from GET .../reference-prefixes/status. */
export function statusSentence(status) {
  if (!status) return null;
  const total = (status.items ?? []).length;
  const label = status.financialYearLabel ?? `FY${status.financialYear}`;
  if (status.complete || status.missing === 0) {
    return { ok: true, text: `All set — every cash book has a prefix for ${label}.` };
  }
  return {
    ok: false,
    text: `${status.missing} of ${total} prefixes missing for FY${status.financialYear}`
      + ` (${label}). A statement import into an account with no prefix is refused.`,
  };
}

/** The financial years to offer, newest first, as `[endYear, label]`. */
export function yearOptions(years, fallbackEndYear = null) {
  const out = new Map();
  for (const y of years ?? []) {
    const endYear = Number(String(y.end_date ?? '').slice(0, 4));
    if (Number.isInteger(endYear) && endYear > 0) out.set(endYear, y.label ?? `FY${endYear}`);
  }
  if (out.size === 0 && Number.isInteger(fallbackEndYear)) {
    for (const y of [fallbackEndYear + 1, fallbackEndYear, fallbackEndYear - 1]) out.set(y, `FY${y}`);
  }
  return [...out.entries()].sort((a, b) => b[0] - a[0]);
}

/** The year that should open selected: the one the entity is in now, else the newest. */
export function defaultYear(options, entityEndDate) {
  const current = Number(String(entityEndDate ?? '').slice(0, 4));
  if (options.some(([y]) => y === current)) return current;
  return options.length ? options[0][0] : null;
}
